// What the Team page shows a tenant login, and which seats a company admin's
// select offers (spec 2026-10-01-agent-seats-policy §6-§7). Pure, so
// node --test covers it; users-ui.jsx and soc-app.jsx import it through
// esbuild. The server owns every rule -- this only decides what to render.

export const SEATS = ['none', 'business', 'developer'];

// Section ids, in page order. Nothing for an admin session or an impersonation token
// (the admin acting AS a tenant). The seat cards need seats on (`seats_enabled`; absent
// from /auth/me while off); the company admin's users, tasks and grants need only the
// role, which follows the tenant's own switch (spec 2026-10-18-full-separation §7.1).
export function teamSections(me) {
  if (!me || me.role !== 'tenant' || me.via_admin) return [];
  const seatsOn = !!me.seats_enabled;
  const out = [];
  const seat = seatsOn && !!me.agent_seat && me.agent_seat !== 'none';
  if (seat) out.push('contact', 'telegram');
  if (seatsOn && me.tenant_admin) out.push('company');
  // The Agent limits card (agent seats PR 3, design handoff 2026-10-02 §4):
  // a seat holder's own threshold, a company admin's ceilings and money list.
  if (seat || (seatsOn && me.tenant_admin)) out.push('limits');
  if (me.tenant_admin) out.push('users', 'tasks', 'permissions');
  return out;
}

export const showsTeam = (me) => teamSections(me).length > 0;

// The seat columns of a company's user list (users-ui.jsx UsersCard): while agent seats
// are on, or for the offline developers' workspace, whose seats count with the knob at 0
// (spec 2026-10-14-offline-installations §3.2; GET /admin/tenants carries `offline`).
// Never on the installation admins' own list.
export const showsSeatColumns = (seatsOn, tenant, adminMode) =>
  !adminMode && (!!seatsOn || !!(tenant && tenant.offline));

// Tal, 2026-10-02: a company admin pauses any task of its company but resumes
// only one its own company's people own -- an installation admin's task reads
// every table when it runs. `companyUserIds` is the company's user list; an
// installation admin is never on it. The server answers 403 otherwise.
export const mayResume = (task, companyUserIds) =>
  task.status === 'paused' && companyUserIds.has(task.owner_user_id);

// Tal, 2026-10-03: a company admin's own ceiling is an installation admin's to
// set (the server answers 403), so its own row reads it. `selfId` is the
// viewer's user id; an installation admin's card passes none.
export const maySetCeiling = (user, selfId) => selfId == null || user.id !== selfId;

// The seats the select offers `user`: its own seat and 'none' always, any
// other while the company's counts leave room. The server refuses the rest
// with 422 anyway; this only keeps a hopeless choice off the list.
export function seatChoices(limits, user) {
  if (!limits) return SEATS.filter((s) => s === 'none' || s === user.agent_seat);
  const held = limits.seats_held - (user.agent_seat !== 'none' ? 1 : 0);
  const devHeld = limits.dev_seats_held - (user.agent_seat === 'developer' ? 1 : 0);
  return SEATS.filter((s) => s === 'none' || s === user.agent_seat
    || (held < limits.max_seats && (s !== 'developer' || devHeld < limits.max_dev_seats)));
}

// The Agent limits card's amount box -- a ceiling, or the viewer's own
// threshold -- read for its PUT. Blank is null: none (a ceiling cleared, a
// threshold unset). Anything that is not a finite number is `undefined`, which
// the card refuses itself: JSON.stringify writes Infinity and NaN as null, so
// a typed 1e999 would reach the server as "clear the ceiling" and be answered
// 200. A negative goes through, so the server's 422 names the limit
// (app/users/limits.py `_amount`).
export function parseAmount(text) {
  const s = String(text ?? '').trim();
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

const amount = (n) => Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 });

// The viewer's own limits as one line. GET /auth/me/agent-policy answers the
// bare {ceiling, threshold, notify} (Task 9), so the line works out what the
// policy reads: it asks above min(threshold, ceiling), and about every amount
// until both are set (limits.effective_threshold, controller ruling
// 2026-10-02); a threshold over the ceiling still reads as the lower.
export function ownLimitText(policy, lang) {
  const he = lang === 'he';
  const { ceiling, threshold } = policy;
  if (ceiling == null) return he ? 'לא נקבעה תקרה: הסוכן שואל על כל סכום.' : 'No ceiling set: every amount asks.';
  const most = he ? `לכל היותר ${amount(ceiling)} (התקרה של מנהל החברה).`
    : `At most ${amount(ceiling)} (your company admin's ceiling).`;
  if (threshold == null) return `${most} ${he ? 'לא נקבע סף: הסוכן שואל על כל סכום.' : 'No threshold set: every amount asks.'}`;
  const above = amount(Math.min(threshold, ceiling));
  return `${most} ${he ? `הסוכן שואל על כל סכום מעל ${above}.` : `Every amount above ${above} asks.`}`;
}
